import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch, useApi, getToken } from '../hooks/useApi.js';
import { fmtAbv } from '../utils/format.js';
import { uiAlert, uiConfirm } from '../utils/dialogs.js';
import PrintButton from '../components/shared/PrintButton.jsx';
import CopyLinkButton from '../components/shared/CopyLinkButton.jsx';
import { useSearchParams } from 'react-router-dom';

// ── Constants ─────────────────────────────────────────────────────────────────
const RECIPE_TABS = [
  { key: 'bulk_liquid', label: 'Bulk Liquid', stagesLabel: 'Production stages' },
  { key: 'packaging',   label: 'Packaging',   stagesLabel: 'Packaging stages'  },
  { key: 'generate_pos', label: 'Generate POs' },
  { key: 'archive', label: 'Archive', stagesLabel: 'Stages' },
];

const STAGE_TYPES = [
  'fermentation', 'distillation', 'compounding', 'maceration',
  'infusion', 'blending', 'filtration', 'maturation', 'packaging', 'other',
];
const EQUIPMENT_TYPES = [
  'still', 'fermenter', 'mash_vessel', 'blending_vessel',
  'filter', 'bottling_line', 'ibc', 'cask', 'other',
];
const SPIRIT_TYPES = [
  'gin', 'whisky', 'vodka', 'rum', 'brandy', 'liqueur',
  'eau_de_vie', 'absinthe', 'neutral_spirit', 'other',
];

// ── UI Primitives ─────────────────────────────────────────────────────────────
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = {
    borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer',
    border: 'none', transition: 'opacity .15s',
    fontSize: size === 'sm' ? 12 : 14,
    padding: size === 'sm' ? '5px 12px' : '9px 18px',
    opacity: props.disabled ? 0.5 : 1,
  };
  const variants = {
    primary: { background: 'var(--pld-green)', color: '#fff' },
    danger:  { background: '#dc2626', color: '#fff' },
    ghost:   { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' },
  };
  return <button {...props} style={{ ...base, ...variants[variant], ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 520, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--text-muted)', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</label>
      {children}
    </div>
  );
}

function Input(props) {
  return <input {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }} />;
}

function SelectEl({ children, ...props }) {
  return <select {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)' }}>{children}</select>;
}

function Textarea({ ...props }) {
  return <textarea {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical', color: 'var(--text)', ...props.style }} />;
}

function Badge({ label, color = '#6b7280', bg = '#f3f4f6' }) {
  return <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: bg, color, whiteSpace: 'nowrap' }}>{label}</span>;
}

function OwnerBadge({ owner_type }) {
  return owner_type === 'CONTRACT'
    ? <Badge label="CONTRACT" color="#1d4ed8" bg="#dbeafe" />
    : <Badge label="PLD" color="#166534" bg="#dcfce7" />;
}

function StatusBadge({ status }) {
  const map = {
    draft:    { bg: '#f3f4f6', color: '#6b7280' },
    active:   { bg: '#dcfce7', color: '#166534' },
    archived: { bg: '#fef3c7', color: '#92400e' },
  };
  const s = map[status] || map.draft;
  return <Badge label={status} color={s.color} bg={s.bg} />;
}

function StageTypeBadge({ type }) {
  const colours = {
    fermentation:  { bg: '#fef3c7', color: '#92400e' },
    distillation:  { bg: '#dbeafe', color: '#1e40af' },
    compounding:   { bg: '#f3e8ff', color: '#6b21a8' },
    maceration:    { bg: '#d1fae5', color: '#065f46' },
    infusion:      { bg: '#d1fae5', color: '#065f46' },
    blending:      { bg: '#e0f2fe', color: '#0369a1' },
    filtration:    { bg: '#f1f5f9', color: '#475569' },
    maturation:    { bg: '#fde8d0', color: '#9a3412' },
    packaging:     { bg: '#fce7f3', color: '#9d174d' },
    other:         { bg: '#f3f4f6', color: '#6b7280' },
  };
  const c = colours[type] || colours.other;
  return <Badge label={type} color={c.color} bg={c.bg} />;
}

function InputTypeBadge({ type }) {
  return type === 'alcoholic'
    ? <Badge label="alcoholic" color="#991b1b" bg="#fee2e2" />
    : <Badge label="non-alcoholic" color="#374151" bg="#f3f4f6" />;
}

function Spinner() {
  return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 80, color: 'var(--text-muted)', fontSize: 14 }}>Loading…</div>;
}

function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : ''; }

// ── Stock Search (for adding ingredients) ─────────────────────────────────────
function StockSearch({ value, onChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const data = await apiFetch(`/stock?search=${encodeURIComponent(q)}&is_active=true`);
      setResults((data.stock_items || []).slice(0, 20));
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query), 280);
    return () => clearTimeout(t);
  }, [query, search]);

  return (
    <div style={{ position: 'relative' }}>
      <Input
        placeholder="Search stock items…"
        value={value ? value.name : query}
        onChange={e => { setQuery(e.target.value); if (value) onChange(null); }}
        style={{ marginBottom: 0 }}
      />
      {!value && results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)', zIndex: 400, maxHeight: 200, overflowY: 'auto' }}>
          {searching && <div style={{ padding: '8px 12px', fontSize: 13, color: 'var(--text-muted)' }}>Searching…</div>}
          {results.map(item => (
            <button
              key={item.id}
              onClick={() => { onChange(item); setQuery(''); setResults([]); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
              onMouseOver={e => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseOut={e => e.currentTarget.style.background = 'none'}
            >
              <span style={{ fontWeight: 500 }}>{item.name}</span>
              <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>{item.sku} · {item.category}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Stage Modal ───────────────────────────────────────────────────────────────
function StageModal({ title, initial = {}, onSave, onClose, saving }) {
  const [form, setForm] = useState({
    name: '', stage_type: 'distillation', equipment_type: '',
    expected_yield_pct: '', duration_hours: '', target_abv: '', notes: '',
    ...initial,
  });
  const [outputItem, setOutputItem] = useState(
    initial.output_stock_item_id
      ? { id: initial.output_stock_item_id, name: initial.output_item_name, sku: initial.output_item_sku, category: initial.output_item_category }
      : null
  );
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleOutputItemChange = (item) => {
    setOutputItem(item);
    setForm(f => ({ ...f, output_stock_item_id: item ? item.id : null }));
  };

  return (
    <Modal title={title} onClose={onClose}>
      <Field label="Stage name *">
        <Input value={form.name} onChange={set('name')} placeholder="e.g. First distillation" />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Stage type *">
          <SelectEl value={form.stage_type} onChange={set('stage_type')}>
            {STAGE_TYPES.map(t => <option key={t} value={t}>{cap(t)}</option>)}
          </SelectEl>
        </Field>
        <Field label="Equipment">
          <SelectEl value={form.equipment_type} onChange={set('equipment_type')}>
            <option value="">— None —</option>
            {EQUIPMENT_TYPES.map(t => <option key={t} value={t}>{cap(t)}</option>)}
          </SelectEl>
        </Field>
        <Field label="Expected yield %">
          <Input type="number" value={form.expected_yield_pct} onChange={set('expected_yield_pct')} placeholder="e.g. 70" />
        </Field>
        <Field label="Duration (hours)">
          <Input type="number" value={form.duration_hours} onChange={set('duration_hours')} placeholder="e.g. 48" />
        </Field>
        <Field label="Target ABV %">
          <Input type="number" value={form.target_abv} onChange={set('target_abv')} placeholder="e.g. 65" />
        </Field>
      </div>
      <Field label="Output stock item (what this stage produces)">
        <StockSearch value={outputItem} onChange={handleOutputItemChange} />
        {outputItem ? (
          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
            <span>{outputItem.sku}{outputItem.category ? ` · ${outputItem.category}` : ''}</span>
            <button type="button" onClick={() => handleOutputItemChange(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', textDecoration: 'underline', fontSize: 12, padding: 0 }}>Clear</button>
          </div>
        ) : (
          <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
            Leave blank for a stage that doesn't produce a standalone tracked item (e.g. adding botanicals mid-ferment). Set this for a stage whose output should carry forward automatically into the next stage's inputs during a production run — such as the wash from fermentation feeding distillation — or for the final stage, the recipe's finished item.
          </div>
        )}
      </Field>
      <Field label="Notes">
        <Textarea rows={3} value={form.notes} onChange={set('notes')} />
      </Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn disabled={saving || !form.name} onClick={() => onSave(form)}>{saving ? 'Saving…' : 'Save stage'}</Btn>
      </div>
    </Modal>
  );
}

// ── Add Ingredient Modal ──────────────────────────────────────────────────────
function AddIngredientModal({ onSave, onClose, saving }) {
  const [item, setItem] = useState(null);
  const [qty, setQty] = useState('');
  const [unit, setUnit] = useState('kg');
  const [inputType, setInputType] = useState('non_alcoholic');
  const [manualCost, setManualCost] = useState('');

  // Auto-detect input type when stock item is selected
  const handleItemSelect = (si) => {
    setItem(si);
    setUnit(si.unit_of_measure || si.unit || 'kg');
    setInputType(si.is_alcoholic ? 'alcoholic' : 'non_alcoholic');
  };

  return (
    <Modal title="Add ingredient" onClose={onClose}>
      <Field label="Stock item *">
        <StockSearch value={item} onChange={handleItemSelect} />
        {item && (
          <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
            {item.sku} · {item.category} · {item.is_alcoholic ? '🍶 alcoholic' : 'non-alcoholic'}
          </div>
        )}
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
        <Field label="Quantity *">
          <Input type="number" step="0.001" value={qty} onChange={e => setQty(e.target.value)} placeholder="0.000" />
        </Field>
        <Field label="Unit">
          <Input value={unit} onChange={e => setUnit(e.target.value)} placeholder="kg" />
        </Field>
      </div>
      <Field label="Input type">
        <SelectEl value={inputType} onChange={e => setInputType(e.target.value)}>
          <option value="non_alcoholic">Non-alcoholic</option>
          <option value="alcoholic">Alcoholic</option>
        </SelectEl>
      </Field>
      <Field label="Cost per unit (optional)">
        <Input type="number" step="0.0001" value={manualCost} onChange={e => setManualCost(e.target.value)} placeholder="Leave blank to use latest PO price" />
        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
          If set, this overrides the price pulled from the item's most recent purchase order.
        </div>
      </Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn disabled={saving || !item || !qty} onClick={() => onSave({
          stock_item_id: item.id, quantity: parseFloat(qty), quantity_unit: unit, input_type: inputType,
          manual_unit_cost: manualCost === '' ? undefined : parseFloat(manualCost),
        })}>
          {saving ? 'Adding…' : 'Add ingredient'}
        </Btn>
      </div>
    </Modal>
  );
}

// ── Normalize to 1L / 1 unit Modal ────────────────────────────────────────────
function NormalizeModal({ recipe, onSave, onClose, saving }) {
  const [basis, setBasis] = useState('');
  const isPackaging = recipe.recipe_type === 'packaging';
  const unitLabel = isPackaging ? 'unit' : 'litre';
  return (
    <Modal title={`Normalize recipe to 1 ${unitLabel}`} onClose={onClose}>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-muted)' }}>
        "{recipe.name}" isn't standardised yet — its ingredient quantities are whatever batch size it was originally written at.
        Enter that batch size below and every ingredient quantity will be divided down to a 1 {unitLabel} reference, so production runs can scale it up automatically by target {isPackaging ? 'quantity' : 'volume'}.
      </p>
      <Field label={`What batch size (in ${unitLabel}s) is this recipe currently written for? *`}>
        <Input type="number" step="0.01" min="0" value={basis} onChange={e => setBasis(e.target.value)} placeholder="e.g. 500" />
      </Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn disabled={saving || !basis || parseFloat(basis) <= 0} onClick={() => onSave(basis)}>
          {saving ? 'Rescaling…' : 'Normalize'}
        </Btn>
      </div>
    </Modal>
  );
}

// ── Print Recipe Modal ────────────────────────────────────────────────────────
function PrintRecipeModal({ recipe, onPrint, onClose }) {
  const isPackaging = recipe.recipe_type === 'packaging';
  const unitLabel = recipe.output_item_unit || (isPackaging ? 'unit(s)' : 'litres');
  const recipeBasis = recipe.expected_output_quantity != null
    ? parseFloat(recipe.expected_output_quantity)
    : (parseFloat(recipe.batch_size_litres) || 1);
  const [qty, setQty] = useState('');
  const parsed = parseFloat(qty);
  const valid = qty === '' || (!isNaN(parsed) && parsed > 0);

  return (
    <Modal title="Print recipe" onClose={onClose}>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-muted)' }}>
        How much {isPackaging ? 'finished product' : unitLabel} do you want to make? Every ingredient quantity on the printout
        will be scaled up or down from this recipe's standard batch ({recipeBasis.toLocaleString(undefined, { maximumFractionDigits: 4 })} {unitLabel}) to match.
      </p>
      <Field label={`Quantity to make (${unitLabel})`}>
        <Input
          type="number" step="any" min="0" autoFocus
          value={qty} onChange={e => setQty(e.target.value)}
          placeholder={`e.g. 500 (leave blank for the standard ${recipeBasis} ${unitLabel} batch)`}
        />
      </Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn variant="ghost" disabled={!valid} onClick={() => { onPrint(qty === '' ? null : parsed); onClose(); }}>
          Preview
        </Btn>
        {valid && (
          <PrintButton docType="recipe_sheet" size="md"
            path={`/recipes/${recipe.id}/print-html${qty !== '' ? `?litres=${encodeURIComponent(parsed)}` : ''}`}
            title={`Recipe ${recipe.name || ''}`.trim()} />
        )}
      </div>
    </Modal>
  );
}

// ── Edit Ingredient Modal ─────────────────────────────────────────────────────
function EditIngredientModal({ ingredient, stages = [], onSave, onClose, saving }) {
  const [qty, setQty] = useState(ingredient.quantity ?? '');
  const [unit, setUnit] = useState(ingredient.quantity_unit || ingredient.unit || '');
  const [inputType, setInputType] = useState(ingredient.input_type || 'non_alcoholic');
  const [manualCost, setManualCost] = useState(
    ingredient.manual_unit_cost != null ? String(ingredient.manual_unit_cost) : ''
  );
  const [stageId, setStageId] = useState(ingredient.recipe_stage_id || '');

  return (
    <Modal title={`Edit ingredient — ${ingredient.item_name}`} onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
        <Field label="Quantity *">
          <Input type="number" step="0.001" value={qty} onChange={e => setQty(e.target.value)} />
        </Field>
        <Field label="Unit">
          <Input value={unit} onChange={e => setUnit(e.target.value)} />
        </Field>
      </div>
      <Field label="Input type">
        <SelectEl value={inputType} onChange={e => setInputType(e.target.value)}>
          <option value="non_alcoholic">Non-alcoholic</option>
          <option value="alcoholic">Alcoholic</option>
        </SelectEl>
      </Field>
      <Field label="Stage">
        <SelectEl value={stageId} onChange={e => setStageId(e.target.value)}>
          <option value="">Unassigned — not linked to any stage</option>
          {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </SelectEl>
      </Field>
      <Field label="Cost per unit (optional override)">
        <Input type="number" step="0.0001" value={manualCost} onChange={e => setManualCost(e.target.value)} placeholder="Leave blank to use latest PO price" />
        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
          {ingredient.cost_source && !ingredient.cost_source?.manual && (
            <>Currently {costSourceLabel(ingredient.unit_cost, ingredient.cost_source)}. </>
          )}
          Clear this field to go back to using the latest purchase order price.
        </div>
      </Field>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn disabled={saving || !qty} onClick={() => onSave({
          quantity: parseFloat(qty), quantity_unit: unit, input_type: inputType,
          manual_unit_cost: manualCost === '' ? null : parseFloat(manualCost),
          recipe_stage_id: stageId === '' ? null : stageId,
        })}>
          {saving ? 'Saving…' : 'Save changes'}
        </Btn>
      </div>
    </Modal>
  );
}

// ── Recipe Modal (create / edit) ──────────────────────────────────────────────
function RecipeModal({ title, initial = {}, onSave, onClose, saving }) {
  const [form, setForm] = useState({
    name: '', spirit_type: 'gin', owner_type: 'PLD',
    status: 'draft', is_confidential: false, notes: '',
    recipe_type: 'bulk_liquid', output_stock_item_id: null,
    ...initial,
  });
  const [outputItem, setOutputItem] = useState(
    initial.output_stock_item_id
      ? { id: initial.output_stock_item_id, name: initial.output_item_name, sku: initial.output_item_sku, category: initial.output_item_category }
      : null
  );
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setCheck = k => e => setForm(f => ({ ...f, [k]: e.target.checked }));

  const handleOutputItemChange = (item) => {
    setOutputItem(item);
    setForm(f => ({ ...f, output_stock_item_id: item ? item.id : null }));
  };

  return (
    <Modal title={title} onClose={onClose}>
      <Field label="Name *">
        <Input value={form.name} onChange={set('name')} placeholder="Recipe name" />
      </Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Spirit type *">
          <SelectEl value={form.spirit_type} onChange={set('spirit_type')}>
            {SPIRIT_TYPES.map(t => <option key={t} value={t}>{cap(t)}</option>)}
          </SelectEl>
        </Field>
        <Field label="Owner type">
          <SelectEl value={form.owner_type} onChange={set('owner_type')}>
            <option value="PLD">PLD</option>
            <option value="CONTRACT">CONTRACT</option>
          </SelectEl>
        </Field>
        <Field label="Recipe type">
          <SelectEl value={form.recipe_type} onChange={set('recipe_type')}>
            {RECIPE_TABS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </SelectEl>
        </Field>
        {initial.id && (
          <Field label="Status">
            <SelectEl value={form.status} onChange={set('status')}>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </SelectEl>
          </Field>
        )}
      </div>
      <Field label="Output stock item (item this recipe produces)">
        <StockSearch value={outputItem} onChange={handleOutputItemChange} />
        {outputItem && (
          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
            <span>{outputItem.sku}{outputItem.category ? ` · ${outputItem.category}` : ''}</span>
            <button type="button" onClick={() => handleOutputItemChange(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', textDecoration: 'underline', fontSize: 12, padding: 0 }}>Clear</button>
          </div>
        )}
      </Field>
      {form.recipe_type === 'packaging' ? (
        <div style={{ marginBottom: 16, fontSize: 12, color: 'var(--text-muted)', background: 'var(--surface-2)', padding: '8px 12px', borderRadius: 'var(--radius)' }}>
          Packaging recipes always produce 1 finished unit (e.g. 1 bottle) from 1 unit of each component. Case quantities are handled separately via Product Specs.
        </div>
      ) : (
        <Field label="Expected output quantity (how much finished product this ingredient list yields)">
          <Input type="number" step="0.0001" min="0" value={form.expected_output_quantity ?? ''} onChange={set('expected_output_quantity')} placeholder={`e.g. 1 (litres of ${outputItem?.name || 'finished product'})`} />
          <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
            Not necessarily the same as the volume of ingredients going in — this is what actually comes out (after any distillation/process loss). Production runs scale off this figure.
          </div>
        </Field>
      )}
      <Field label="Notes">
        <Textarea rows={3} value={form.notes || ''} onChange={set('notes')} />
      </Field>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer', marginBottom: 16 }}>
        <input type="checkbox" checked={form.is_confidential} onChange={setCheck('is_confidential')} />
        Mark as confidential
      </label>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn disabled={saving || !form.name} onClick={() => onSave(form)}>{saving ? 'Saving…' : 'Save'}</Btn>
      </div>
    </Modal>
  );
}

// ── Stage Card ────────────────────────────────────────────────────────────────
function StageCard({ stage, isFirst, isLast, onMoveUp, onMoveDown, onEdit, onDelete, onAddIngredient, onRemoveIngredient, onEditIngredient }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', marginBottom: 10, background: 'var(--surface)', overflow: 'hidden' }}>
      {/* Stage header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: 'var(--surface-2)', borderBottom: expanded ? '1px solid var(--border)' : 'none', cursor: 'pointer' }} onClick={() => setExpanded(e => !e)}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', minWidth: 20 }}>{stage.stage_order}</span>
        <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{stage.name}</span>
        <StageTypeBadge type={stage.stage_type} />
        {stage.equipment_type && <Badge label={cap(stage.equipment_type)} color="#475569" bg="#f1f5f9" />}
        {stage.target_abv && <Badge label={`${fmtAbv(stage.target_abv)}% ABV`} color="#1d4ed8" bg="#dbeafe" />}
        {stage.expected_yield_pct && <Badge label={`${stage.expected_yield_pct}% yield`} color="#065f46" bg="#d1fae5" />}
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{stage.ingredients?.length || 0} ingredients</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
          <Btn size="sm" variant="ghost" onClick={onMoveUp}  disabled={isFirst}  style={{ padding: '3px 8px' }}>↑</Btn>
          <Btn size="sm" variant="ghost" onClick={onMoveDown} disabled={isLast}  style={{ padding: '3px 8px' }}>↓</Btn>
          <Btn size="sm" variant="ghost" onClick={onEdit}  style={{ padding: '3px 8px' }}>Edit</Btn>
          <Btn size="sm" variant="danger" onClick={onDelete} style={{ padding: '3px 8px' }}>Delete</Btn>
        </span>
        <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={{ padding: '12px 16px' }}>
          {stage.notes && (
            <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>{stage.notes}</p>
          )}
          {/* Ingredients table */}
          {stage.ingredients?.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10, fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600, color: 'var(--text-muted)' }}>Ingredient</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600, color: 'var(--text-muted)' }}>SKU</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 600, color: 'var(--text-muted)' }}>Qty</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600, color: 'var(--text-muted)' }}>Unit</th>
                  <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600, color: 'var(--text-muted)' }}>Type</th>
                  <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 600, color: 'var(--text-muted)' }}>Cost</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {stage.ingredients.map(ing => (
                  <tr key={ing.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 8px' }}>{ing.item_name}</td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 12 }}>{ing.sku}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{parseFloat(ing.quantity).toLocaleString(undefined, { maximumFractionDigits: 3 })}</td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-muted)' }}>{ing.quantity_unit || ing.unit}</td>
                    <td style={{ padding: '6px 8px' }}><InputTypeBadge type={ing.input_type} /></td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {ing.line_cost != null
                        ? <span title={costSourceLabel(ing.unit_cost, ing.cost_source)}>{money(ing.line_cost)}</span>
                        : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }} title="No priced purchase order line found for this item">no PO price</span>}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Btn size="sm" variant="ghost" onClick={() => onEditIngredient(ing)} style={{ padding: '2px 8px', marginRight: 4 }}>Edit</Btn>
                      <Btn size="sm" variant="danger" onClick={() => onRemoveIngredient(ing.id)} style={{ padding: '2px 8px' }}>Remove</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-muted)' }}>No ingredients assigned to this stage yet.</p>
          )}
          <Btn size="sm" variant="ghost" onClick={onAddIngredient}>+ Add ingredient</Btn>
        </div>
      )}
    </div>
  );
}

// ── Unassigned Ingredients ────────────────────────────────────────────────────
function UnassignedIngredients({ recipeId, ingredients, onRefresh, onEdit }) {
  const [removing, setRemoving] = useState(null);

  const removeIngredient = async (id) => {
    setRemoving(id);
    try {
      await apiFetch(`/recipes/${recipeId}/ingredients/${id}`, { method: 'DELETE' });
      onRefresh();
    } catch (e) { uiAlert(e.message); }
    finally { setRemoving(null); }
  };

  if (!ingredients?.length) return null;

  return (
    <div style={{ marginTop: 16, border: '1px dashed var(--border)', borderRadius: 'var(--radius-lg)', padding: 16 }}>
      <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>
        Unassigned ingredients ({ingredients.length}) — not linked to any stage
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600, color: 'var(--text-muted)' }}>Ingredient</th>
            <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600, color: 'var(--text-muted)' }}>SKU</th>
            <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 600, color: 'var(--text-muted)' }}>Qty</th>
            <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600, color: 'var(--text-muted)' }}>Unit</th>
            <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600, color: 'var(--text-muted)' }}>Type</th>
            <th style={{ textAlign: 'right', padding: '4px 8px', fontWeight: 600, color: 'var(--text-muted)' }}>Cost</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {ingredients.map(ing => (
            <tr key={ing.id} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '6px 8px' }}>{ing.item_name}</td>
              <td style={{ padding: '6px 8px', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 12 }}>{ing.sku}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>{parseFloat(ing.quantity).toLocaleString(undefined, { maximumFractionDigits: 3 })}</td>
              <td style={{ padding: '6px 8px', color: 'var(--text-muted)' }}>{ing.quantity_unit || ing.unit}</td>
              <td style={{ padding: '6px 8px' }}><InputTypeBadge type={ing.input_type} /></td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                {ing.line_cost != null
                  ? <span title={costSourceLabel(ing.unit_cost, ing.cost_source)}>{money(ing.line_cost)}</span>
                  : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }} title="No priced purchase order line found for this item">no PO price</span>}
              </td>
              <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                <Btn size="sm" variant="ghost" onClick={() => onEdit(ing)} style={{ padding: '2px 8px', marginRight: 4 }}>Edit</Btn>
                <Btn size="sm" variant="danger" disabled={removing === ing.id} onClick={() => removeIngredient(ing.id)} style={{ padding: '2px 8px' }}>
                  {removing === ing.id ? '…' : 'Remove'}
                </Btn>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Recipe Detail ─────────────────────────────────────────────────────────────
function money(n) {
  return n == null ? null : `£${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Readable label for an ingredient's cost_source (recipeController.costIngredients /
// supplierPricingService.pickCheapestSupplier) — covers every source shape
// it can return: a manual override, a quantity-break tier or a flat price
// from whichever approved supplier came out cheapest, a plain latest-PO
// price, or the item's standard_cost fallback.
function costSourceLabel(unit_cost, cost_source) {
  if (!cost_source) return undefined;
  const price = money(unit_cost);
  if (cost_source.manual) return `${price}/unit — manual override`;
  if (cost_source.price_break) {
    return `${price}/unit — price break (${cost_source.min_quantity}+)${cost_source.supplier_name ? ` from ${cost_source.supplier_name}` : ''}`;
  }
  if (cost_source.approved_supplier_price) {
    return `${price}/unit${cost_source.supplier_name ? ` — ${cost_source.supplier_name}` : ' — approved supplier price'}`;
  }
  if (cost_source.po_ref) return `${price}/unit — from ${cost_source.po_ref}`;
  if (cost_source.standard) return `${price}/unit — standard cost`;
  return `${price}/unit`;
}

function RecipeDetail({ recipe, onRefresh, onRecipeUpdated, stagesLabel = 'Production stages' }) {
  const [stages, setStages] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [loadingStages, setLoadingStages] = useState(true);
  const [costSummary, setCostSummary] = useState(null);

  // Modals
  const [showAddStage, setShowAddStage] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [addIngStage, setAddIngStage] = useState(null); // stage to add ingredient to
  const [showEditRecipe, setShowEditRecipe] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [showNormalize, setShowNormalize] = useState(false);
  const [showAddUnassigned, setShowAddUnassigned] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [saving, setSaving] = useState(false);
  const [estimateQty, setEstimateQty] = useState('');

  const loadStages = useCallback(async () => {
    if (!recipe) return;
    setLoadingStages(true);
    try {
      const [stagesData, ingredientsData] = await Promise.all([
        apiFetch(`/recipes/${recipe.id}/stages`),
        apiFetch(`/recipes/${recipe.id}/ingredients`),
      ]);
      // Costed ingredients (unit_cost/line_cost from latest PO price) come
      // back from /ingredients; merge them onto the per-stage ingredient
      // lists from /stages by id, since /stages doesn't cost independently.
      const costMap = {};
      for (const ing of ingredientsData.ingredients || []) costMap[ing.id] = ing;
      const withCost = (list) => (list || []).map(i => ({ ...i, ...(costMap[i.id] || {}) }));

      setStages((stagesData.stages || []).map(s => ({ ...s, ingredients: withCost(s.ingredients) })));
      // Unassigned = ingredients with no recipe_stage_id
      setUnassigned((ingredientsData.ingredients || []).filter(i => !i.recipe_stage_id));
      setCostSummary(ingredientsData.cost_summary || null);
    } catch (e) { console.error(e); }
    finally { setLoadingStages(false); }
  }, [recipe]);

  useEffect(() => { loadStages(); }, [loadStages]);

  // ── Stage CRUD ──────────────────────────────────────────────────────────────
  const handleAddStage = async (form) => {
    setSaving(true);
    try {
      await apiFetch(`/recipes/${recipe.id}/stages`, { method: 'POST', body: form });
      setShowAddStage(false);
      loadStages();
    } catch (e) { uiAlert(e.message); }
    finally { setSaving(false); }
  };

  const handleEditStage = async (form) => {
    setSaving(true);
    try {
      await apiFetch(`/recipes/${recipe.id}/stages/${editingStage.id}`, { method: 'PUT', body: form });
      setEditingStage(null);
      loadStages();
    } catch (e) { uiAlert(e.message); }
    finally { setSaving(false); }
  };

  const handleDeleteStage = async (stageId) => {
    if (!await uiConfirm('Delete this stage? Ingredients will be unassigned, not deleted.')) return;
    try {
      await apiFetch(`/recipes/${recipe.id}/stages/${stageId}`, { method: 'DELETE' });
      loadStages();
    } catch (e) { uiAlert(e.message); }
  };

  const handleReorder = async (fromIdx, direction) => {
    const newStages = [...stages];
    const toIdx = fromIdx + direction;
    if (toIdx < 0 || toIdx >= newStages.length) return;
    [newStages[fromIdx], newStages[toIdx]] = [newStages[toIdx], newStages[fromIdx]];
    // Reassign stage_order
    const updated = newStages.map((s, i) => ({ ...s, stage_order: i + 1 }));
    setStages(updated);
    try {
      await apiFetch(`/recipes/${recipe.id}/stages/reorder`, {
        method: 'PATCH',
        body: { order: updated.map(s => ({ id: s.id, stage_order: s.stage_order })) },
      });
    } catch (e) { uiAlert(e.message); loadStages(); }
  };

  // ── Ingredient CRUD ─────────────────────────────────────────────────────────
  const handleAddIngredient = async (form) => {
    setSaving(true);
    try {
      await apiFetch(`/recipes/${recipe.id}/stages/${addIngStage.id}/ingredients`, { method: 'POST', body: form });
      setAddIngStage(null);
      loadStages();
    } catch (e) { uiAlert(e.message); }
    finally { setSaving(false); }
  };

  // Adds an ingredient directly to the recipe with no stage assigned — for
  // recipes that don't need stage-level breakdown, or ingredients you'll
  // assign to a stage later.
  const handleAddUnassignedIngredient = async (form) => {
    setSaving(true);
    try {
      await apiFetch(`/recipes/${recipe.id}/ingredients`, {
        method: 'POST',
        body: { ...form, input_type: form.input_type || 'non_alcoholic' },
      });
      setShowAddUnassigned(false);
      loadStages();
    } catch (e) { uiAlert(e.message); }
    finally { setSaving(false); }
  };

  const handleRemoveIngredient = async (ingredientId) => {
    if (!await uiConfirm('Remove this ingredient?')) return;
    try {
      await apiFetch(`/recipes/${recipe.id}/ingredients/${ingredientId}`, { method: 'DELETE' });
      loadStages();
    } catch (e) { uiAlert(e.message); }
  };

  const handleEditIngredient = async (form) => {
    setSaving(true);
    try {
      await apiFetch(`/recipes/${recipe.id}/ingredients/${editingIngredient.id}`, { method: 'PUT', body: form });
      setEditingIngredient(null);
      loadStages();
    } catch (e) { uiAlert(e.message); }
    finally { setSaving(false); }
  };

  // ── Recipe update ───────────────────────────────────────────────────────────
  const handleEditRecipe = async (form) => {
    setSaving(true);
    try {
      await apiFetch(`/recipes/${recipe.id}`, { method: 'PUT', body: { name: form.name, spirit_type: form.spirit_type, is_confidential: form.is_confidential, notes: form.notes, recipe_type: form.recipe_type, output_stock_item_id: form.output_stock_item_id || null, expected_output_quantity: form.expected_output_quantity } });
      if (form.status !== recipe.status) {
        await apiFetch(`/recipes/${recipe.id}/status`, { method: 'PATCH', body: { status: form.status } });
      }
      setShowEditRecipe(false);
      onRecipeUpdated();
    } catch (e) { uiAlert(e.message); }
    finally { setSaving(false); }
  };

  const handleNormalize = async (currentBasisLitres) => {
    setSaving(true);
    try {
      const res = await apiFetch(`/recipes/${recipe.id}/normalize-to-1l`, {
        method: 'POST',
        body: { current_basis_litres: parseFloat(currentBasisLitres) },
      });
      setShowNormalize(false);
      onRecipeUpdated();
      loadStages();
      uiAlert(`Rescaled ${res.ingredients_rescaled} ingredient line(s) to a 1L basis.`);
    } catch (e) { uiAlert(e.message); }
    finally { setSaving(false); }
  };

  async function openRecipePrintPreview(targetQty) {
    // Authenticated route (Bearer token, no cookie session) — a plain
    // window.open() straight to the API URL sends no Authorization header
    // and just 401s. Fetch it ourselves with the token, then hand the
    // browser a blob: URL to open instead (same pattern as PO Print/Preview).
    try {
      const qs = targetQty !== '' && targetQty != null ? `?litres=${encodeURIComponent(targetQty)}` : '';
      const res = await fetch(`/api/recipes/${recipe.id}/print-html${qs}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load print view (HTTP ${res.status})`);
      }
      const html = await res.text();
      const blobUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      const win = window.open(blobUrl, '_blank');
      if (!win) throw new Error('Pop-up blocked — allow pop-ups for this site to use Print Recipe.');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      uiAlert(err.message);
    }
  }

  if (!recipe) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
        Select a recipe to view details
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
      {/* Recipe header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 700 }}>{recipe.name}</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <OwnerBadge owner_type={recipe.owner_type} />
            <StatusBadge status={recipe.status} />
            <Badge label={cap(recipe.spirit_type)} color="#374151" bg="#f3f4f6" />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>v{recipe.version}</span>
            {recipe.is_confidential && <Badge label="Confidential" color="#9d174d" bg="#fce7f3" />}
            {recipe.output_item_name && (
              <Badge label={`Produces: ${recipe.output_item_name}`} color="#0369a1" bg="#e0f2fe" />
            )}
            {parseFloat(recipe.batch_size_litres) === 1
              ? <Badge label={recipe.recipe_type === 'packaging' ? 'Standardised to 1 unit' : 'Standardised to 1L'} color="#065f46" bg="#d1fae5" />
              : <Badge label={`⚠ Ingredients written for ${recipe.batch_size_litres}L — not standardised`} color="#b45309" bg="#fef3c7" />
            }
          </div>
          {recipe.notes && <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>{recipe.notes}</p>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {parseFloat(recipe.batch_size_litres) !== 1 && (
            <Btn size="sm" variant="ghost" onClick={() => setShowNormalize(true)}>
              {recipe.recipe_type === 'packaging' ? 'Normalize to 1 unit' : 'Normalize to 1L'}
            </Btn>
          )}
          <CopyLinkButton path={`/recipes?recipe=${recipe.id}`} />
          <Btn size="sm" variant="ghost" onClick={() => setShowPrint(true)}>🖨 Print recipe</Btn>
          <Btn size="sm" variant="ghost" onClick={() => setShowEditRecipe(true)}>Edit recipe</Btn>
        </div>
      </div>

      {/* Output yield — what this recipe's ingredients actually produce.
          Called out prominently because production scaling depends on it
          and a missing figure here means scaling can't be trusted. */}
      <div style={{
        marginBottom: 16, padding: '10px 14px', borderRadius: 'var(--radius-lg)',
        border: recipe.expected_output_quantity != null ? '1px solid var(--border)' : '1px solid #fbbf24',
        background: recipe.expected_output_quantity != null ? 'var(--surface)' : '#fffbeb',
        fontSize: 13,
      }}>
        {recipe.expected_output_quantity != null ? (
          <span>
            Produces <strong>{parseFloat(recipe.expected_output_quantity).toLocaleString(undefined, { maximumFractionDigits: 4 })} {recipe.output_item_unit || (recipe.recipe_type === 'packaging' ? 'unit(s)' : 'litres')}</strong>
            {recipe.output_item_name ? ` of ${recipe.output_item_name}` : ' of finished product'} from this ingredient list.
          </span>
        ) : (
          <span style={{ color: '#b45309', fontWeight: 600 }}>
            ⚠ No output quantity set on this recipe — there's no record of how much finished product these ingredients actually yield.
            Production run scaling will assume no process loss until you set this. <Btn size="sm" variant="ghost" onClick={() => setShowEditRecipe(true)} style={{ marginLeft: 4, padding: '2px 8px' }}>Set it now</Btn>
          </span>
        )}
      </div>

      {/* Cost summary — from latest purchase order price per ingredient */}
      {costSummary && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap',
          padding: '12px 16px', borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)', background: 'var(--surface)',
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>
              Estimated cost per {recipe.batch_size_litres}{recipe.recipe_type === 'packaging' ? ' unit' : 'L'} batch
            </div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{money(costSummary.total_cost)}</div>
          </div>
          {costSummary.cost_per_output_unit != null && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>
                Cost per {recipe.output_item_unit || (recipe.recipe_type === 'packaging' ? 'unit' : 'litre')} of output
              </div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{money(costSummary.cost_per_output_unit)}</div>
            </div>
          )}
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Based on the most recent purchase order price for each ingredient.
            {!costSummary.all_ingredients_priced && (
              <span style={{ color: '#b45309', fontWeight: 600 }}>
                {' '}⚠ {costSummary.unpriced_count} ingredient{costSummary.unpriced_count === 1 ? '' : 's'} have no priced purchase order yet — total is incomplete.
              </span>
            )}
          </div>

          {/* Batch-size cost estimator — scales the priced total to an arbitrary
              target output quantity, using the same scale-factor convention as
              production run scaling and Generate POs (expected_output_quantity
              falling back to batch_size_litres). */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            paddingLeft: 16, marginLeft: 4, borderLeft: '1px solid var(--border)',
          }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>
                Estimate cost for batch size
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={estimateQty}
                  onChange={(e) => setEstimateQty(e.target.value)}
                  placeholder="e.g. 500"
                  style={{ width: 110 }}
                />
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {recipe.output_item_unit || (recipe.recipe_type === 'packaging' ? 'unit(s)' : 'litres')} of output
                </span>
              </div>
            </div>
            {estimateQty !== '' && !isNaN(parseFloat(estimateQty)) && (() => {
              const recipeBasisLitres = recipe.expected_output_quantity != null
                ? parseFloat(recipe.expected_output_quantity)
                : (parseFloat(recipe.batch_size_litres) || 1);
              const scaleFactor = parseFloat(estimateQty) / recipeBasisLitres;
              const estimatedCost = costSummary.total_cost * scaleFactor;
              return (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>
                    Estimated cost
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{money(estimatedCost)}</div>
                  {recipe.expected_output_quantity == null && (
                    <div style={{ fontSize: 11, color: '#b45309', fontWeight: 600, marginTop: 2, maxWidth: 260 }}>
                      ⚠ No output quantity set — this assumes no process loss. <Btn size="sm" variant="ghost" onClick={() => setShowEditRecipe(true)} style={{ padding: '1px 6px' }}>Set it now</Btn>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Stages */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{stagesLabel}</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn size="sm" variant="ghost" onClick={() => setShowAddUnassigned(true)}>+ Add ingredient</Btn>
          <Btn size="sm" onClick={() => setShowAddStage(true)}>+ Add stage</Btn>
        </div>
      </div>

      {loadingStages ? <Spinner /> : (
        <>
          {stages.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: 14 }}>
              No stages yet. Add the first stage to start building this recipe.
            </div>
          )}
          {stages.map((stage, idx) => (
            <StageCard
              key={stage.id}
              stage={stage}
              isFirst={idx === 0}
              isLast={idx === stages.length - 1}
              onMoveUp={() => handleReorder(idx, -1)}
              onMoveDown={() => handleReorder(idx, 1)}
              onEdit={() => setEditingStage(stage)}
              onDelete={() => handleDeleteStage(stage.id)}
              onAddIngredient={() => setAddIngStage(stage)}
              onRemoveIngredient={handleRemoveIngredient}
              onEditIngredient={setEditingIngredient}
            />
          ))}
          <UnassignedIngredients
            recipeId={recipe.id}
            ingredients={unassigned}
            onRefresh={loadStages}
            onEdit={setEditingIngredient}
          />
        </>
      )}

      {/* Modals */}
      {showAddStage && (
        <StageModal title="Add stage" onSave={handleAddStage} onClose={() => setShowAddStage(false)} saving={saving} />
      )}
      {editingStage && (
        <StageModal title="Edit stage" initial={editingStage} onSave={handleEditStage} onClose={() => setEditingStage(null)} saving={saving} />
      )}
      {addIngStage && (
        <AddIngredientModal
          onSave={handleAddIngredient}
          onClose={() => setAddIngStage(null)}
          saving={saving}
        />
      )}
      {editingIngredient && (
        <EditIngredientModal
          ingredient={editingIngredient}
          stages={stages}
          onSave={handleEditIngredient}
          onClose={() => setEditingIngredient(null)}
          saving={saving}
        />
      )}
      {showNormalize && (
        <NormalizeModal
          recipe={recipe}
          onSave={handleNormalize}
          onClose={() => setShowNormalize(false)}
          saving={saving}
        />
      )}
      {showPrint && (
        <PrintRecipeModal
          recipe={recipe}
          onPrint={openRecipePrintPreview}
          onClose={() => setShowPrint(false)}
        />
      )}
      {showAddUnassigned && (
        <AddIngredientModal
          onSave={handleAddUnassignedIngredient}
          onClose={() => setShowAddUnassigned(false)}
          saving={saving}
        />
      )}
      {showEditRecipe && (
        <RecipeModal
          title="Edit recipe"
          initial={recipe}
          onSave={handleEditRecipe}
          onClose={() => setShowEditRecipe(false)}
          saving={saving}
        />
      )}
    </div>
  );
}

// ── Recipe List ───────────────────────────────────────────────────────────────
function RecipeList({ onSelect, selectedId, recipeType, archiveMode = false }) {
  const [filters, setFilters] = useState({ owner_type: '', spirit_type: '', status: '', search: '' });
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(t);
  }, [filters.search]);

  const params = new URLSearchParams();
  if (recipeType)         params.set('recipe_type', recipeType);
  if (filters.owner_type) params.set('owner_type', filters.owner_type);
  if (filters.spirit_type) params.set('spirit_type', filters.spirit_type);
  // Archive tab always shows archived recipes of every type; the regular
  // tabs never show archived ones mixed in with active/draft any more —
  // they get their own dedicated tab instead of cluttering daily browsing.
  if (archiveMode) params.set('status', 'archived');
  else if (filters.status) params.set('status', filters.status);
  if (debouncedSearch)    params.set('search', debouncedSearch);

  const { data, loading, refetch } = useApi(`/recipes?${params}`);
  const recipes = (data?.recipes || []).filter(r => archiveMode || r.status !== 'archived');

  const setFilter = k => e => setFilters(f => ({ ...f, [k]: e.target.value }));

  return (
    <div style={{ width: 300, minWidth: 260, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: 16, borderBottom: '1px solid var(--border)' }}>
        <Input placeholder={archiveMode ? 'Search archived recipes…' : 'Search recipes…'} value={filters.search} onChange={setFilter('search')} style={{ marginBottom: 8 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          <SelectEl value={filters.owner_type} onChange={setFilter('owner_type')}>
            <option value="">All owners</option>
            <option value="PLD">PLD</option>
            <option value="CONTRACT">CONTRACT</option>
          </SelectEl>
          {!archiveMode && (
            <SelectEl value={filters.status} onChange={setFilter('status')}>
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
            </SelectEl>
          )}
          <SelectEl value={filters.spirit_type} onChange={setFilter('spirit_type')} style={{ gridColumn: '1 / -1' }}>
            <option value="">All spirit types</option>
            {SPIRIT_TYPES.map(t => <option key={t} value={t}>{cap(t)}</option>)}
          </SelectEl>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading && <Spinner />}
        {!loading && recipes.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No recipes found</div>
        )}
        {recipes.map(r => (
          <button
            key={r.id}
            onClick={() => onSelect(r)}
            style={{
              display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px',
              background: selectedId === r.id ? 'var(--surface-2)' : 'none',
              border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer',
              borderLeft: selectedId === r.id ? '3px solid var(--pld-green)' : '3px solid transparent',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--text)' }}>{r.name}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <OwnerBadge owner_type={r.owner_type} />
              <StatusBadge status={r.status} />
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{cap(r.spirit_type)}</span>
            </div>
          </button>
        ))}
      </div>

      {/* New recipe button pinned to bottom */}
      {!archiveMode && (
        <div style={{ padding: 12, borderTop: '1px solid var(--border)' }}>
          <NewRecipeButton defaultRecipeType={recipeType} onCreated={(r) => { refetch(); onSelect(r); }} />
        </div>
      )}
    </div>
  );
}

// ── New Recipe Button + Modal ──────────────────────────────────────────────────
function NewRecipeButton({ onCreated, defaultRecipeType = 'bulk_liquid' }) {
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      const data = await apiFetch('/recipes', {
        method: 'POST',
        body: {
          name: form.name, spirit_type: form.spirit_type, owner_type: form.owner_type,
          is_confidential: form.is_confidential, notes: form.notes,
          recipe_type: form.recipe_type || defaultRecipeType,
          output_stock_item_id: form.output_stock_item_id || null,
          expected_output_quantity: form.expected_output_quantity,
        },
      });
      setShow(false);
      onCreated(data.recipe);
    } catch (e) { uiAlert(e.message); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Btn style={{ width: '100%' }} onClick={() => setShow(true)}>+ New recipe</Btn>
      {show && (
        <RecipeModal
          title="New recipe"
          initial={{ recipe_type: defaultRecipeType }}
          onSave={handleCreate}
          onClose={() => setShow(false)}
          saving={saving}
        />
      )}
    </>
  );
}

// ── Recipe Search (for the PO generator's multi-recipe picker) ────────────────
function RecipeSearch({ onSelect, excludeIds = [] }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const data = await apiFetch(`/recipes?search=${encodeURIComponent(q)}&status=active`);
      setResults((data.recipes || []).filter(r => !excludeIds.includes(r.id)).slice(0, 20));
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, [excludeIds]);

  useEffect(() => {
    const t = setTimeout(() => search(query), 280);
    return () => clearTimeout(t);
  }, [query, search]);

  return (
    <div style={{ position: 'relative' }}>
      <Input
        placeholder="Search recipes to add…"
        value={query}
        onChange={e => setQuery(e.target.value)}
      />
      {results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)', zIndex: 400, maxHeight: 240, overflowY: 'auto' }}>
          {searching && <div style={{ padding: '8px 12px', fontSize: 13, color: 'var(--text-muted)' }}>Searching…</div>}
          {results.map(r => (
            <button
              key={r.id}
              onClick={() => { onSelect(r); setQuery(''); setResults([]); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
              onMouseOver={e => e.currentTarget.style.background = 'var(--surface-2)'}
              onMouseOut={e => e.currentTarget.style.background = 'none'}
            >
              <span style={{ fontWeight: 500 }}>{r.name}</span>
              <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>{cap(r.spirit_type)} · v{r.version}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Generate Purchase Orders from Recipes ──────────────────────────────────────
function GeneratePOsPanel() {
  const [requirements, setRequirements] = useState([]); // [{recipe_id, recipe_name, target_quantity}]
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lineState, setLineState] = useState({}); // stock_item_id -> { included, quantity_ordered, unit_price, supplier_id, remember }
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);

  const addRecipe = (r) => {
    setRequirements(reqs => [...reqs, { recipe_id: r.id, recipe_name: r.name, target_quantity: '' }]);
    setPreview(null); setCreateResult(null);
  };
  const setQty = (idx, v) => {
    setRequirements(reqs => reqs.map((r, i) => i === idx ? { ...r, target_quantity: v } : r));
  };
  const removeRow = (idx) => {
    setRequirements(reqs => reqs.filter((_, i) => i !== idx));
    setPreview(null); setCreateResult(null);
  };

  async function runPreview() {
    setError(''); setCreateResult(null);
    const valid = requirements.filter(r => r.target_quantity && parseFloat(r.target_quantity) > 0);
    if (valid.length === 0) { setError('Add at least one recipe with a target quantity.'); return; }
    setLoading(true);
    try {
      const res = await apiFetch('/procurement/recipe-pos/preview', {
        method: 'POST',
        body: { requirements: valid.map(r => ({ recipe_id: r.recipe_id, target_quantity: parseFloat(r.target_quantity) })) },
      });
      setPreview(res);
      const ls = {};
      for (const group of res.grouped_by_supplier) {
        for (const line of group.lines) {
          ls[line.stock_item_id] = {
            included: true,
            quantity_ordered: line.recommended_qty,
            unit_price: line.suggested_unit_price ?? '',
            supplier_id: group.supplier_id,
            remember: false,
          };
        }
      }
      for (const line of res.unassigned_items) {
        ls[line.stock_item_id] = {
          included: true,
          quantity_ordered: line.recommended_qty,
          unit_price: line.suggested_unit_price ?? '',
          supplier_id: '',
          remember: true,
        };
      }
      setLineState(ls);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const setLine = (stockItemId, patch) => {
    setLineState(ls => ({ ...ls, [stockItemId]: { ...ls[stockItemId], ...patch } }));
  };

  async function createPOs() {
    setError('');
    const allLines = [...(preview.grouped_by_supplier.flatMap(g => g.lines)), ...preview.unassigned_items];
    const bySupplier = {};
    const rememberSuppliers = [];
    for (const line of allLines) {
      const ls = lineState[line.stock_item_id];
      if (!ls || !ls.included) continue;
      if (!ls.supplier_id) {
        setError(`"${line.item_name}" needs a supplier before you can generate POs — assign one or uncheck it.`);
        return;
      }
      const qty = parseFloat(ls.quantity_ordered);
      if (!qty || qty <= 0) continue;
      if (!bySupplier[ls.supplier_id]) bySupplier[ls.supplier_id] = { supplier_id: ls.supplier_id, lines: [] };
      bySupplier[ls.supplier_id].lines.push({
        stock_item_id: line.stock_item_id,
        quantity_ordered: qty,
        unit_price: ls.unit_price !== '' ? parseFloat(ls.unit_price) : null,
      });
      if (ls.remember) rememberSuppliers.push({ stock_item_id: line.stock_item_id, supplier_id: ls.supplier_id });
    }
    const orders = Object.values(bySupplier).filter(o => o.lines.length > 0);
    if (orders.length === 0) { setError('Nothing to order — every line is either fully covered or unchecked.'); return; }

    const sourceNote = `Auto-generated to fulfil: ${preview.resolved_requirements.map(r => `${r.recipe_name} (${r.target_quantity})`).join(', ')}`;

    setCreating(true);
    try {
      const res = await apiFetch('/procurement/recipe-pos/create', {
        method: 'POST',
        body: { orders, remember_suppliers: rememberSuppliers, source_note: sourceNote },
      });
      setCreateResult(res.purchase_orders);
      setPreview(null);
      setRequirements([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>Generate Purchase Orders from Recipes</h2>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
          Pick one or more recipes and how much of each you need to make. Requirements are scaled per recipe, combined,
          netted against current held stock, then grouped into draft purchase orders by supplier — nothing is sent, and
          you can review and adjust everything before it's created. Items with a minimum stock level set (Stock → item →
          Reorder Threshold) are also topped up if this order would otherwise leave them below it, even when there's no
          outright shortfall. Order quantities are rounded up to an item's minimum order quantity where one's set, and
          each supplier's group is checked against their minimum order value.
        </p>
      </div>

      {createResult && (
        <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: 'var(--radius)', padding: 16 }}>
          <div style={{ fontWeight: 700, color: '#166534', marginBottom: 6 }}>
            {createResult.length} draft purchase order{createResult.length === 1 ? '' : 's'} created
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {createResult.map(po => (
              <span key={po.id} style={{ fontFamily: 'monospace', fontSize: 13, background: '#fff', border: '1px solid #86efac', borderRadius: 6, padding: '3px 8px' }}>{po.po_ref}</span>
            ))}
          </div>
          <p style={{ margin: '8px 0 0', fontSize: 12, color: '#166534' }}>Find them under Stock → Purchase Orders to review, adjust, and send.</p>
        </div>
      )}

      {/* Recipe + quantity rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {requirements.map((r, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{r.recipe_name}</div>
            <Input
              type="number" min="0.001" step="0.001" placeholder="Target quantity"
              value={r.target_quantity} onChange={e => setQty(idx, e.target.value)}
              style={{ width: 180 }}
            />
            <button onClick={() => removeRow(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 18, lineHeight: 1 }}>×</button>
          </div>
        ))}
        <RecipeSearch onSelect={addRecipe} excludeIds={requirements.map(r => r.recipe_id)} />
      </div>

      {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}

      <div>
        <Btn onClick={runPreview} disabled={loading || requirements.length === 0}>
          {loading ? 'Calculating…' : 'Calculate Requirements'}
        </Btn>
      </div>

      {preview && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {preview.warnings.length > 0 && (
            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 'var(--radius)', padding: 12 }}>
              {preview.warnings.map((w, i) => <p key={i} style={{ margin: i ? '6px 0 0' : 0, fontSize: 13, color: '#92400e' }}>⚠ {w}</p>)}
            </div>
          )}

          {[...preview.grouped_by_supplier.map(g => ({ ...g, unassigned: false })), preview.unassigned_items.length > 0 ? { supplier_id: '__unassigned', supplier_name: 'No preferred supplier set', lines: preview.unassigned_items, unassigned: true } : null].filter(Boolean).map(group => {
            // Recompute live from the editable line state, not the
            // preview snapshot, so ticking a line off or changing its qty
            // updates the minimum-order-value check immediately.
            let liveValue = 0;
            let liveHasUnpriced = false;
            for (const line of group.lines) {
              const ls = lineState[line.stock_item_id];
              if (!ls?.included) continue;
              const qty = parseFloat(ls.quantity_ordered) || 0;
              if (ls.unit_price !== '' && ls.unit_price != null) {
                liveValue += qty * parseFloat(ls.unit_price);
              } else {
                liveHasUnpriced = true;
              }
            }
            const belowMinValue = !group.unassigned && group.minimum_order_value != null && liveValue < group.minimum_order_value;
            return (
            <div key={group.supplier_id} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', background: group.unassigned ? '#fef3c7' : 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: group.unassigned ? '#92400e' : 'var(--text)' }}>{group.supplier_name}</span>
                {!group.unassigned && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    Order value: £{liveValue.toFixed(2)}{liveHasUnpriced ? '+' : ''}
                    {group.minimum_order_value != null && ` · min £${fmt(group.minimum_order_value)}`}
                  </span>
                )}
              </div>
              {belowMinValue && (
                <div style={{ padding: '8px 14px', background: '#fef3c7', color: '#92400e', fontSize: 12, borderBottom: '1px solid var(--border)' }}>
                  ⚠ This order is below {group.supplier_name}'s minimum order value of £{fmt(group.minimum_order_value)} — currently £{liveValue.toFixed(2)}{liveHasUnpriced ? ' (plus unpriced lines)' : ''}. Add more to this order, or it may not be processed.
                </div>
              )}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', width: 28 }}></th>
                    <th style={{ textAlign: 'left', padding: '8px 12px' }}>Item</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px' }}>Required</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px' }}>Available</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px' }}>Order Qty</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px' }}>Unit Price</th>
                    {group.unassigned && <th style={{ textAlign: 'left', padding: '8px 12px' }}>Supplier</th>}
                  </tr>
                </thead>
                <tbody>
                  {group.lines.map(line => {
                    const ls = lineState[line.stock_item_id] || {};
                    return (
                      <tr key={line.stock_item_id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px 12px' }}>
                          <input type="checkbox" checked={!!ls.included} onChange={e => setLine(line.stock_item_id, { included: e.target.checked })} />
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {line.item_name}
                            {line.below_minimum && (
                              <span title={`This item has a minimum stock level of ${line.reorder_threshold} ${line.unit}. Ordering just enough for this recipe would leave it below that, so the order quantity has been topped up to bring it back to the minimum.`}>
                                <Badge label="below minimum" color="#92400e" bg="#fef3c7" />
                              </span>
                            )}
                            {line.moq_applied && (
                              <span title={`This item is only sold in lots of ${line.minimum_order_quantity} ${line.unit}. The order quantity was rounded up from ${line.pre_moq_qty} ${line.unit} to a full lot.`}>
                                <Badge label="rounded to lot size" color="#1e40af" bg="#dbeafe" />
                              </span>
                            )}
                          </div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                            {line.sku} · from {line.breakdown.map(b => b.recipe_name).join(', ')}
                            {line.reorder_threshold != null && ` · min ${line.reorder_threshold} ${line.unit}`}
                          </div>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>{line.required_qty} {line.unit}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>{line.available_qty} {line.unit}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                          <Input type="number" min="0" step="0.001" value={ls.quantity_ordered ?? ''} onChange={e => setLine(line.stock_item_id, { quantity_ordered: e.target.value })} style={{ width: 100, textAlign: 'right' }} />
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                          <Input type="number" min="0" step="0.01" value={ls.unit_price ?? ''} onChange={e => setLine(line.stock_item_id, { unit_price: e.target.value })} style={{ width: 90, textAlign: 'right' }} placeholder="—" />
                          {line.price_source && (
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                              {line.price_source === 'standard cost' ? 'standard cost' : `from ${line.price_source}`}
                            </div>
                          )}
                        </td>
                        {group.unassigned && (
                          <td style={{ padding: '8px 12px' }}>
                            <SupplierPicker value={ls.supplier_id} onChange={v => setLine(line.stock_item_id, { supplier_id: v })} />
                            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                              <input type="checkbox" checked={!!ls.remember} onChange={e => setLine(line.stock_item_id, { remember: e.target.checked })} />
                              Remember for next time
                            </label>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            );
          })}

          {preview.fully_covered_items.length > 0 && (
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, color: 'var(--text-muted)' }}>Already covered by held stock (and stays at or above its minimum, where one is set)</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {preview.fully_covered_items.map(i => `${i.item_name} (${i.required_qty} ${i.unit} needed, ${i.available_qty} available${i.reorder_threshold != null ? `, min ${i.reorder_threshold} ${i.unit}` : ''})`).join(' · ')}
              </div>
            </div>
          )}

          {preview.not_purchasable_items.length > 0 && (
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, color: 'var(--text-muted)' }}>Not purchasable — produced internally, not bought from a supplier</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {preview.not_purchasable_items.map(i => `${i.item_name} (${i.required_qty} ${i.unit} needed)`).join(' · ')}
              </div>
            </div>
          )}

          {(preview.grouped_by_supplier.length > 0 || preview.unassigned_items.length > 0) && (
            <div>
              <Btn onClick={createPOs} disabled={creating}>
                {creating ? 'Creating…' : 'Create Draft Purchase Orders'}
              </Btn>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SupplierPicker({ value, onChange }) {
  const { data } = useApi('/suppliers');
  const suppliers = data?.suppliers ?? [];
  return (
    <SelectEl value={value || ''} onChange={e => onChange(e.target.value)} style={{ width: 200 }}>
      <option value="">— select supplier —</option>
      {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
    </SelectEl>
  );
}

// ── Tab Bar ───────────────────────────────────────────────────────────────────
function TabBar({ activeKey, onChange }) {
  return (
    <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--surface)', paddingLeft: 24 }}>
      {RECIPE_TABS.map(tab => {
        const active = tab.key === activeKey;
        return (
          <button
            key={tab.key}
            onClick={() => onChange(tab.key)}
            style={{
              padding: '10px 20px',
              fontSize: 13,
              fontWeight: active ? 700 : 500,
              color: active ? 'var(--pld-green)' : 'var(--text-muted)',
              background: 'none',
              border: 'none',
              borderBottom: active ? '2px solid var(--pld-green)' : '2px solid transparent',
              cursor: 'pointer',
              marginBottom: -1,
              transition: 'color .15s',
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Recipes() {
  const [activeTab, setActiveTab] = useState('bulk_liquid');
  const [selected, setSelected]   = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // The open recipe is in the address (?recipe=…), so a copied link opens it.
  const [params, setParams] = useSearchParams();
  const linked = params.get('recipe');
  useEffect(() => {
    if (!linked || selected?.id === linked) return;
    apiFetch(`/recipes/${linked}`).then((res) => {
      const r = res?.recipe ?? res?.data?.recipe ?? res;
      if (!r?.id) return;
      setActiveTab(r.status === 'archived' ? 'archive' : (r.recipe_type === 'packaging' ? 'packaging' : 'bulk_liquid'));
      setSelected(r);
    }).catch((e) => uiAlert(`That recipe link didn't open: ${e.message}`));
  }, [linked]); // eslint-disable-line react-hooks/exhaustive-deps
  const hadSelection = useRef(false);
  useEffect(() => {
    const id = selected?.id || null;
    if (!id) { if (hadSelection.current && linked) setParams({}, { replace: true }); return; } // not before a linked recipe has loaded
    hadSelection.current = true;
    if (id !== linked) setParams({ recipe: id }, { replace: true });
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabDef = RECIPE_TABS.find(t => t.key === activeTab);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSelected(null); // clear selection when switching tabs
  };

  // After an edit, reload the open recipe too — not just the list — so a
  // new name or status shows straight away.
  const handleRecipeUpdated = async () => {
    setRefreshKey(k => k + 1);
    if (!selected?.id) return;
    try {
      const res = await apiFetch(`/recipes/${selected.id}`);
      const fresh = res?.recipe ?? res?.data?.recipe;
      if (fresh) setSelected(fresh);
    } catch { /* the list still refreshes */ }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--header-height) - 48px)', margin: -24, overflow: 'hidden' }}>
      <TabBar activeKey={activeTab} onChange={handleTabChange} />
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {activeTab === 'generate_pos' ? (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <GeneratePOsPanel />
          </div>
        ) : (
          <>
            <RecipeList
              key={`${activeTab}-${refreshKey}`}
              recipeType={activeTab === 'archive' ? undefined : activeTab}
              archiveMode={activeTab === 'archive'}
              onSelect={setSelected}
              selectedId={selected?.id}
            />
            <RecipeDetail
              key={selected?.id}
              recipe={selected}
              stagesLabel={tabDef?.stagesLabel}
              onRefresh={() => setRefreshKey(k => k + 1)}
              onRecipeUpdated={handleRecipeUpdated}
            />
          </>
        )}
      </div>
    </div>
  );
}
